import type { RegistrationFormState, RegistrationResponse, LoginResponse, VerifyEmailResponse } from '../types/registration'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.ok) {
    return res.json() as Promise<T>
  }

  let detail: string | undefined
  try {
    const body = await res.json()
    detail = body?.detail
  } catch {
    // ignore parse errors
  }

  if (res.status === 409 && detail === 'email_already_registered') {
    throw new Error('email_already_registered')
  }

  if (res.status === 429) {
    throw new Error('rate_limit_exceeded')
  }

  throw new Error(detail ?? `Request failed with status ${res.status}`)
}

/**
 * Check whether an email address is already registered.
 * Uses a dedicated GET endpoint — does NOT attempt registration.
 * Returns true if the email is available, false if it is taken.
 */
export async function checkEmailAvailability(email: string): Promise<boolean> {
  const res = await fetch(
    `${BASE_URL}/api/register/check-email?email=${encodeURIComponent(email)}`,
    { method: 'GET' },
  )

  if (res.ok) {
    const body = await res.json() as { available: boolean }
    return body.available
  }

  // Surface unexpected errors
  let detail: string | undefined
  try {
    const body = await res.json()
    detail = body?.detail
  } catch {
    // ignore parse errors
  }
  throw new Error(detail ?? `Unexpected status ${res.status} checking email availability`)
}

/**
 * Submit the full registration form.
 * Throws with message 'email_already_registered' on 409,
 * 'rate_limit_exceeded' on 429, or a descriptive message on other errors.
 */
export async function submitRegistration(
  data: RegistrationFormState,
): Promise<RegistrationResponse> {
  // Never send confirmPassword to the server — it is a client-side
  // validation field only.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { confirmPassword, ...payload } = data

  const res = await fetch(`${BASE_URL}/api/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Send credentials so the server can set an httpOnly cookie
    credentials: 'include',
    body: JSON.stringify(payload),
  })

  return handleResponse<RegistrationResponse>(res)
}

/**
 * Retrieve the Google OAuth consent URL for the registration popup.
 * Passes the relay URL so the backend redirects the popup back to this exact
 * origin — required for BroadcastChannel to work (it is origin-scoped).
 */
export async function getGoogleOAuthUrl(): Promise<{ url: string; state: string }> {
  // BASE_URL is '/' in dev and '/app/' in production (Vite base path config).
  // Build the relay URL relative to the current origin so the backend redirects
  // the popup to the same origin as this parent window.
  const relayUrl = window.location.origin + import.meta.env.BASE_URL + 'oauth-relay'
  const res = await fetch(
    `${BASE_URL}/api/register/google/oauth-url?relay_url=${encodeURIComponent(relayUrl)}`,
    { method: 'GET' },
  )

  return handleResponse<{ url: string; state: string }>(res)
}

/**
 * Persist Google OAuth credentials for an existing location.
 * Called from the dashboard after the OAuth popup completes.
 * Requires a valid JWT in localStorage.
 */
export interface SaveGoogleCredentialsResult {
  location_id: string
  has_google_credentials: boolean
  gbp_ids_discovered: boolean
}

export async function saveGoogleCredentials(
  locationId: string,
  encryptedCredentials: string,
): Promise<SaveGoogleCredentialsResult> {
  const jwt = localStorage.getItem('jwt_token')
  if (!jwt) throw new Error('Not authenticated')

  const res = await fetch(
    `${BASE_URL}/api/locations/${encodeURIComponent(locationId)}/google-credentials`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ encrypted_credentials: encryptedCredentials }),
    },
  )

  if (!res.ok) {
    let detail: string | undefined
    try { detail = (await res.json()).detail } catch { /* ignore */ }
    throw new Error(detail ?? `Failed to save credentials (${res.status})`)
  }

  return res.json() as Promise<SaveGoogleCredentialsResult>
}


/**
 * Request a password-reset email. Always resolves (backend returns 200
 * regardless of whether the email is registered, to prevent enumeration).
 * Throws only on rate-limit errors.
 */
export async function forgotPassword(email: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (res.status === 429) throw new Error('rate_limit_exceeded')
  // All other statuses are treated as success to prevent email enumeration
}

/**
 * Set a new password using the token from the reset email.
 * Throws 'invalid_or_expired_token' on 400, 'rate_limit_exceeded' on 429.
 */
export async function resetPassword(token: string, new_password: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password }),
  })
  if (res.status === 429) throw new Error('rate_limit_exceeded')
  if (res.status === 400) throw new Error('invalid_or_expired_token')
  if (!res.ok) throw new Error('reset_failed')
}

/**
 * Authenticate an existing customer with email and password.
 * Returns a JWT token, customer ID, and email_verified flag on success.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  if (res.status === 401) throw new Error('invalid_credentials')
  if (res.status === 429) throw new Error('rate_limit_exceeded')

  return handleResponse<LoginResponse>(res)
}

/**
 * Consume a verification token from the email link.
 * Throws 'invalid_token' on 400.
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const res = await fetch(
    `${BASE_URL}/api/register/verify-email?token=${encodeURIComponent(token)}`,
    { method: 'GET' },
  )
  if (res.status === 400) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.detail ?? 'invalid_token')
  }
  return handleResponse<VerifyEmailResponse>(res)
}

/**
 * Re-send the verification email. Always resolves — backend never reveals
 * whether the address is registered to prevent enumeration.
 */
export async function resendVerification(email: string): Promise<void> {
  await fetch(`${BASE_URL}/api/register/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
}
